import { Box, styled } from '@mui/material';
import { FunctionComponent, SVGProps, useMemo } from 'react';

import { ENOPOrganType, EResourceType, ETransportIconType } from '../../enums';
import {
  CarIcon,
  CarPlaneIcon,
  CaseIcon,
  GearIcon,
  HeartIcon,
  LiverIcon,
  LungsIcon,
  NurseIcon,
  PlaneIcon,
  ScalpelIcon,
} from '../../icons';

interface Props {
  icon: ENOPOrganType | EResourceType | ETransportIconType;
  color?: string;
  iconColor?: string;
  pinned?: boolean;
  shadow?: boolean;
  shape?: 'circle' | 'rhombus';
  borderStyle?: string;
  size?: number | string;
  iconSize?: number | string;
}

const PREFIX = 'CC-CircleIcon';
export const circleIconClasses = {
  root: `${PREFIX}-root`,
  shape: `${PREFIX}-shape`,
  circle: `${PREFIX}-circle`,
  rhombus: `${PREFIX}-rhombus`,
  shadow: `${PREFIX}-shadow`,
  pin: `${PREFIX}-pin`,
  iconWrapper: `${PREFIX}-iconWrapper`,
} as const;

type RootProps = {
  wrapperSize: string;
  shapeSize: string;
  color: string;
  iconColor: string;
  borderStyle?: string;
  shadow?: boolean;
  shape?: 'circle' | 'rhombus';
  shapeSizeNum: number;
  iconSize: string;
  pinned?: boolean;
};

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop =>
    ![
      'wrapperSize',
      'shapeSize',
      'color',
      'iconColor',
      'borderStyle',
      'shadow',
      'shape',
      'shapeSizeNum',
      'iconSize',
      'pinned',
    ].includes(prop as string),
})<RootProps>(({
  wrapperSize,
  shapeSize,
  color,
  iconColor,
  borderStyle,
  shapeSizeNum,
  iconSize,
}) => {
  const borderWidth = Math.floor((shapeSizeNum / 5) * 2);

  return {
    [`&.${circleIconClasses.root}`]: {
      position: 'relative',
      width: wrapperSize,
      height: wrapperSize,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    },

    [`& .${circleIconClasses.shape}`]: {
      position: 'relative',
      width: shapeSize,
      height: shapeSize,
      backgroundColor: color,
      color: iconColor,
      border: borderStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    },

    [`& .${circleIconClasses.circle}`]: {
      borderRadius: '50%',
    },

    [`& .${circleIconClasses.rhombus}`]: {
      transform: 'rotate(45deg)',

      '& > div svg': {
        transform: 'rotate(-45deg)',
      },
    },

    [`& .${circleIconClasses.shadow}`]: {
      boxShadow: '0 0 5px 1px rgba(0, 0, 0, 0.15)',
    },

    [`& .${circleIconClasses.pin}`]: {
      position: 'absolute',
      bottom: `-${Math.floor(shapeSizeNum / 3)}px`,
      borderStyle: 'solid',
      borderColor: `${color} transparent transparent transparent`,
      borderWidth: `${borderWidth / 2}px`,
    },

    [`& .${circleIconClasses.iconWrapper}`]: {
      width: iconSize,
      height: iconSize,
      minWidth: '12px',
      minHeight: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };
});

export const ICONS: Record<
  ENOPOrganType | EResourceType | ETransportIconType,
  FunctionComponent<SVGProps<SVGSVGElement>>
> = {
  [ENOPOrganType.Heart]: HeartIcon,
  [ENOPOrganType.Liver]: LiverIcon,
  [ENOPOrganType.Lungs]: LungsIcon,
  [EResourceType.Specialist]: NurseIcon,
  [EResourceType.Device]: CaseIcon,
  [EResourceType.Surgeon]: ScalpelIcon,
  [ETransportIconType.groundOnly]: CarIcon,
  [ETransportIconType.airNonMao]: CarPlaneIcon,
  [ETransportIconType.airMao]: PlaneIcon,
};

const getSize = (
  size: number | string,
  shape: string,
  hasBorder: boolean,
): number => {
  const sizeNum = typeof size === 'number' ? size : parseFloat(size);
  const currentSize = hasBorder ? sizeNum + 4 : sizeNum;

  if (shape === 'circle') {
    return currentSize * 1.154;
  }
  if (shape === 'rhombus') {
    return currentSize * 0.9246;
  }

  return currentSize;
};

export const CircleIcon = ({
  icon,
  size = 30,
  color = '#FF975D',
  iconColor = '#000000',
  pinned = false,
  shadow = false,
  shape = 'circle',
  borderStyle = '',
  iconSize,
}: Props) => {
  const Icon = useMemo(() => ICONS[icon], [icon]);

  const isNumberSize = typeof size === 'number';

  const wrapperSize = isNumberSize
    ? `${getSize(size, 'circle', !!borderStyle)}px`
    : size;

  const shapeSizeNum = isNumberSize
    ? getSize(size, shape, !!borderStyle)
    : parseFloat(size);

  const shapeSize = isNumberSize ? `${shapeSizeNum}px` : size;

  let resolvedIconSize: string;
  if (iconSize === null || iconSize === undefined) {
    resolvedIconSize = '70%';
  } else if (typeof iconSize === 'number') {
    resolvedIconSize = `${iconSize}px`;
  } else {
    resolvedIconSize = iconSize;
  }

  const shapeClassName = `${circleIconClasses.shape} ${circleIconClasses[shape]}${shadow ? ` ${circleIconClasses.shadow}` : ''}`;

  return (
    <Root
      className={circleIconClasses.root}
      wrapperSize={wrapperSize}
      shapeSize={shapeSize}
      color={color}
      iconColor={iconColor}
      borderStyle={borderStyle}
      shadow={shadow}
      shape={shape}
      shapeSizeNum={shapeSizeNum}
      iconSize={resolvedIconSize}
      pinned={pinned}
    >
      <div className={shapeClassName}>
        {pinned && <div className={circleIconClasses.pin} />}

        <div className={circleIconClasses.iconWrapper}>
          {Icon ? (
            <Icon
              width="100%"
              height="100%"
              preserveAspectRatio="xMidYMid meet"
            />
          ) : (
            <GearIcon
              width="100%"
              height="100%"
              preserveAspectRatio="xMidYMid meet"
            />
          )}
        </div>
      </div>
    </Root>
  );
};
